import React, { useState, useContext, ReactNode, useEffect, useMemo } from 'react';
import { Form, Alert, Tooltip, OverlayTrigger, Card, Button, Row, Col, Badge, Container, Dropdown } from 'react-bootstrap';
import { RPWDataReader, getDataReader } from '../../livedataFuncs';
import { useAppSelector } from '../../redux/hooks';
import { useDispatch } from "react-redux";
import { lddStateObject, setLawyerListState, setShowRenAIModal } from '../../redux/reducer';
import styles from './Summary.module.scss';
import { MortgageSolicitorType, MortgageSummaryType, PurchaserSummaryType, ThirdPartySummaryType } from '../../SideDrawer/sideTypes';
import { AppCodes, lddGlobalSessionInfo, MenuItemType, NavObjectType, PopupPageId, ProvincesShortCode, purfileController } from '../navigationTypes';
import { NavigationContext } from "../../Context/NavigationContext";
import DropdownItem from 'react-bootstrap/esm/DropdownItem';
import { getDetailsList } from '../../SideDrawer/menus/details';
import { FormatDateDataType } from '../../util/genericDateAndTimeFuncs';
import { getAPSDisplayAbbr } from '../../fileToolbar/RenAI/renaiFuncs';
import { TrimFull } from '../../util/rpwCommonFuncs';
import { runInnerFunction } from '../../compatManager';
import { getSolicitorLabel } from '../../SideDrawer/sidedrawer';

const PreIcon = (props: {cond: boolean, children: ReactNode, hideIcon?: boolean}) => {
    return <span>
        {!props.hideIcon && <i className={`me-2 fa-solid ${props.cond?`fa-check-circle ${styles.iconGood}`:`fa-diamond-exclamation ${styles.iconBad}`}`}></i>}
        {props.children}
    </span>
}

function CheckEmpty(field: string){
    return field > "" ? field : "---";
}

function AddressIsDefault(address: string, appCode: string){
    const defaults = {
        "ON": ", Ontario",
        "BC": "BC",
        "AB": ", Alberta",
        "NS": "",
        "MB": "MB",
        "SK": "SK"
    }
    return address === defaults[appCode];
}

function formatPhoneNumber(v="", datatype:'phone'|'fax' = 'phone'): string {
	var phone = TrimFull(v);
	if (phone == null || phone == "")
		return phone;
	phone = phone.toLowerCase();
	var ext = "";
	var p1 = phone.indexOf("e");
	if (p1 > 1) {
		ext = phone.substring(p1);
		ext = ext.replace(/[^\d]/g, '');
		phone = TrimFull(phone.substring(0, p1));
	}
	phone = phone.replace(/[^\d]/g, '');
	var p2 = phone.length;
	if (p2 < 7 || p2 == 8 || p2 ==9) {
		return phone;
	} else {
		switch (p2) {
			case 7:
				v = phone.substr(0, 3) + "-" + phone.substring(3);
				break;
			case 10:
				v = "(" + phone.substr(0, 3) + ") " + phone.substr(3, 3) + "-" + phone.substring(6);
				break;
			default:
				v = "+" + phone.substr(0, p2 - 10) + " (" + phone.substr(p2 - 10, 3) + ") " + phone.substr(p2 - 7, 3) + "-" + phone.substring(p2 - 4);
				break;
		}
	}
	if (datatype == "phone" && ext != "")
		v = v + " ext. " + ext;
	return v
}

const Summary: React.FC = () => {
    const livedataRootContainer = useAppSelector((state: lddStateObject) => state.currentNavigation.livedataRootContainer);
    const SESSION_INFO: lddGlobalSessionInfo = useAppSelector((g) => g.SessionInfo);
    const nav = useContext(NavigationContext);
    const dispatch = useDispatch();
    const [detailsList, setDetailsList] = useState([]);
    const [showWelcomeAlert, setShowWelcomeAlert] = useState<boolean>(false);
    const [titleSearchData, setTitleSearchData] = useState<Array<string>>([]);

    const DataReaderObj: RPWDataReader = getDataReader(livedataRootContainer.dataRoot);
    const PSM = DataReaderObj.getPSM();
    const appID = DataReaderObj.getAppId();
    const provinceCode = DataReaderObj.getAppCode();
    const appCode = SESSION_INFO.appCode;

    const SUMMARY_LEARN_MORE_URL = "https://lawyerdonedeal.zohodesk.com/portal/en/kb/ldd/articles/summary-screen-overview";

    const lenderOnly = livedataRootContainer.dataRoot.getAttribute("ActingForLenderOnly") === "1";
    const purchaseMoney = livedataRootContainer.dataRoot.getAttribute("PurchaseMoneyMortgage") === "1";
    const lenderSolicitors = DataReaderObj.getLenderSolicitors();

    const hideBuyerTile = PSM === "M" && lenderOnly;
    const hideSellerTile = PSM === "M" && !lenderOnly;
    const showSellerLegal = hideBuyerTile && purchaseMoney;
    const showOtherLegal = hideSellerTile && lenderSolicitors.length > 0;

    useEffect(() => {
        const ignoreSummaryMsg = window.localStorage.getItem("ignoreSummaryWelcomeMsg");
        setShowWelcomeAlert(ignoreSummaryMsg !== "true");
        getDetailsList(provinceCode as ProvincesShortCode, "", false).then((result) => setDetailsList(result));

        // idk if we need to hit every time we load the screen maybe there is some field in the xml that tells if history exists or not? 
        // if(provinceCode === "ON"){
        // // if(provinceCode === "ON" && DataReaderObj.getNodeInformation("TeranetHistory").length == 0) {
        //     fetchTeranet().then(result => {
        //         if(!result) return;
        //         let pins = [];
        //         const hist = result.children[0]?.children;
        //         if(!hist) return;
        //         for(const node of hist){
        //             if(node.getAttribute("type") === "GetParcelRegister") pins.push(node.getAttribute("pin"));
        //         }
        //         setTitleSearchData(pins);
        //     })
        // }
        setTitleSearchData(DataReaderObj.getTitleSearchPinHistory())
    }, [])

    async function fetchTeranet(): Promise<XMLDocument> {
        let url = `TeranetHistory.asp?id=${appID}&SessionID=${SESSION_INFO.sid}&compat=1`
        return await new Promise(resolve => {
            var xhr = new XMLHttpRequest();
            xhr.open("GET", url, true);
            xhr.onload = function () {
                resolve(xhr.responseXML);
            };
            xhr.onerror = function () {
                resolve(undefined);
                console.error("** An error occurred during the XMLHttpRequest");
            };
            xhr.send();
        })
    }

    const handleWelcomeAlertDismiss = () => {
        window.localStorage.setItem("ignoreSummaryWelcomeMsg", "true");
        setShowWelcomeAlert(false);
    }

    const getNav = (label: string, sublabel?: string): NavObjectType => {
        let first = detailsList.find(x => x.label === label && (x.appCode == undefined || checkDetailsListAppCode(provinceCode, x.appCode)));
        if(sublabel){
            first = first?.subItems?.find(x => x.label === sublabel && (x.appCode == undefined || checkDetailsListAppCode(provinceCode, x.appCode)));
        }
        return first?.navigate;
    }

    const getController = (label: string, sublabel?: string ): purfileController => {
        return getNav(label, sublabel)?.controller || {};
    }

    const getLink = (label: string, sublabel?: string): string => {
        // console.log(getNav(label, sublabel)?.url)
        return getNav(label, sublabel)?.url as string || "";
    }

    const LField = (props: {text: string, txtF?: string, navF?: () => void, condF?: (s: string) => boolean}) => {
        const navFunc = props.navF ?? (() => {return});
        const empty = props.text > "" && props.text !== "0.00";
        const content = empty?
            <span onClick={navFunc}>{props.text}</span>:
            <a onClick={navFunc}>{props.txtF}</a>
        return <>
            {content}
        </>
    }

    const CardItem = (props: {label: string, children?: ReactNode}) => {
        return <>
            <Form.Label>{props.label}</Form.Label>
            <span className={styles.editIcon}>{props.children}</span>
        </>
    }

    const TaxToggle = React.forwardRef<HTMLDivElement, {children: ReactNode, onClick: any}>((props, ref) => {
    return <div
        className="btn-link-text"
        ref={ref}
        onClick={(e) => {
        e.preventDefault();
        props.onClick(e);
        }}>
        <i className='fa-solid fa-magnifying-glass'></i>
        Tax
    </div>
    });

    const OptionsToggle = React.forwardRef<HTMLDivElement, {onClick: any, text?: string}>((props, ref) => {
    return <div
        className="btn-link-text"
        ref={ref}
        onClick={(e) => {
        e.preventDefault();
        props.onClick(e);
        }}>
        {props.text || <i className='fa-duotone fa-circle-plus'></i>}
    </div>
    });

    const OptionsDropdown = (props: {options: Array<MenuItemType>, nav: (navObject: NavObjectType) => void, text?: string}) => { 
        return <Dropdown align={'end'}>
            <Dropdown.Toggle as={OptionsToggle} text={props.text}/>
            <Dropdown.Menu>
                {props.options.map((item) => 
                    <DropdownItem key={item.label} onClick={() => props.nav(item.navigate)}>{item.icon && <i className={item.icon}/>}{item.label}</DropdownItem>
                )}
            </Dropdown.Menu>
        </Dropdown>}

    const PropertyCard = () => {
        const taxLinks = useMemo(() => {
            let dd = detailsList.find(x => x.label === "Due Diligence");
            let links = dd?.subItems.find(x => x.label === "Tax Search" && checkDetailsListAppCode(provinceCode, x.appCode));
            return links?.dropdownOptions || [];
        }, [detailsList])

        const address=DataReaderObj.getPropertyAddress();
        const addressDefault = !address || AddressIsDefault(address, provinceCode);
        const propertyType=DataReaderObj.getPropertyType();
        const pins=DataReaderObj.getAllPins();
        const insuranceInfo=DataReaderObj.getTitleInsuranceSummary();
        const hasTitleInsurance = insuranceInfo.provider > "";
        // Teranet History in the xml may not always be set
        const titleSearches = DataReaderObj.getTitleSearchPinHistory();
        const extraProperties = DataReaderObj.getExtraPropertiesAddress();

        const navProperty = provinceCode==="BC"?() => navigate("Property Details", "Property"): () => navigate("Property");
        const navTitle = () => navigate("Title Insurance");
        let navTitleSearch = () => navigate("Title Search", "Due Diligence")

        const showAdditionalSearch = appID !== "ON";
        let additionalSearchTitle: string, titleSearchHeader = "Title Search";
        let additionalSearchContent: ReactNode, titleSearchContent: ReactNode = <a>Search</a>;
        let hideTitleSearchIcons = true;
        switch(provinceCode){
            case "ON":
                hideTitleSearchIcons = false;
                break;
            case "BC":
                additionalSearchTitle = "Other Title Search";
                additionalSearchContent = <>
                    {["Manufactured Home", "First Nations Land", "Archived Searches"].map((label) => 
                    <div><a className={styles.underline} onClick={() => navigate(label, "Due Diligence")}>{label}</a></div>)}
                </>
                titleSearchHeader = "LTSA Search";
                navTitleSearch = () => navigate("LTSA", "Due Diligence")
                titleSearchContent = <a className={styles.underline} onClick={navTitleSearch}>Search</a>
                hideTitleSearchIcons = false;
                break;
            case "AB":
                additionalSearchTitle = "SPIN2";
                additionalSearchContent = <span>
                    <i className='fa-regular fa-up-right-from-square'></i>
                    <a className={styles.underline} target="_blank" href={getLink("Due Diligence", "SPIN2")}>Search</a>
                </span>
                titleSearchContent = <><a className={styles.underline} onClick={navTitleSearch}>import </a>or<a className={styles.underline} onClick={navProperty}> add manually</a></>
                break;
            case "MB":
                additionalSearchTitle = "Tax Search";
                additionalSearchContent = <span>
                    <i className='fa-regular fa-file-import'></i>
                    <a className={styles.underline} onClick={() => navigate("Tax Search", "Due Diligence")}>Import</a>
                </span>
                break;
            case "NS":
                additionalSearchTitle = "Property Online";
                additionalSearchContent = <span>
                    <i className='fa-regular fa-file-import'></i>
                    <a className={styles.underline} target="_blank" href={getLink("Due Diligence", "Property Online")}>Import</a>
                </span>
                break;
            case "SK":
                additionalSearchTitle = "ISC Property";
                additionalSearchContent = <span>
                    <i className='fa-regular fa-file-import'></i>
                    <a className={styles.underline} target="_blank" href={getLink("Due Diligence", "ISC Property Search")}>Search</a>
                </span>
                break;
            default:
        }
        const showExtraProperties = provinceCode === "BC";
        const showTaxLinks = ["ON", "AB", "SK"].indexOf(provinceCode) > -1;
        const showInsurance = PSM === "P" || PSM === "M";
        const showFireInsurance = !(PSM === "S" && ["BC", "NS"].includes(provinceCode));
        const apsMsg = `Import ${getAPSDisplayAbbr(provinceCode)} with RenAI`

        return <Card>
            {addressDefault?
                <EmptyField icon="fa-calendar-lines-pen" txt="Property Details" linkText='Add Property Details' navFunc={navProperty}>
                    {provinceCode === "ON" && <Button onClick={navTitleSearch}><i className='fa fa-magnifying-glass'></i>Title Search</Button>}
                    <Button variant="warning" onClick={() => dispatch(setShowRenAIModal(true))}><i className="fa-solid fa-sparkles"></i>{apsMsg}</Button>
                </EmptyField>
                :
                <>
                    <Card.Header>
                        <Card.Title>
                            <i className='fa-duotone fa-house'></i>
                            Property
                        </Card.Title>
                        <Button variant="outline-primary" className="text-truncate" onClick={() => dispatch(setShowRenAIModal(true))}>
                            <i className="fa-solid fa-sparkles"></i>
                            {apsMsg}
                        </Button>
                    </Card.Header>

                    <Card.Body>
                        <Container>
                            <Row className="mb-3">
                                <Col xs="12" className={styles.glow} onClick={navProperty}> 
                                    <CardItem label="Address">
                                        <LField text={address} txtF='An unexpected error occurred'></LField>
                                    </CardItem>

                                    {propertyType &&
                                        <div className="mt-1">
                                            <Badge bg="info" pill>{propertyType}</Badge>
                                        </div>
                                    }
                                </Col>
                            </Row>

                            <Row>
                                <Col className={"col-6 " + styles.glow} onClick={navTitleSearch}>
                                    <CardItem label={titleSearchHeader}>
                                        {pins.length?
                                                <>
                                                    {pins.map((item, index) => {
                                                        let searched = titleSearchData.includes(item);
                                                        return <span style={{ display: 'inline-block', minWidth: '55%' }} key={index}>
                                                            <PreIcon cond={searched} hideIcon={hideTitleSearchIcons}>
                                                                {item}
                                                            </PreIcon>
                                                        </span>})
                                                    }
                                                </>
                                            :
                                            <PreIcon cond={false}>
                                                {titleSearchContent}
                                            </PreIcon>
                                        }
                                    </CardItem>
                                </Col>

                                {showAdditionalSearch && additionalSearchTitle &&
                                    <Col>
                                        <CardItem label={additionalSearchTitle}>
                                            {additionalSearchContent}
                                        </CardItem>
                                    </Col> 
                                }
                            </Row>
                            
                            <Row className="mb-3">
                                <Col>
                                    <hr />
                                </Col>
                            </Row>

                            <Row className="mb-3">
                                {showInsurance && 
                                <Col className={styles.glow} onClick={navTitle}>
                                    <CardItem label="Title Insurance">
                                        {hasTitleInsurance?
                                            <>
                                                <PreIcon cond={true} hideIcon={true}>
                                                    <LField text={insuranceInfo.provider}></LField>
                                                </PreIcon>
                                            </>
                                            :
                                            <PreIcon cond={false} >
                                                <a>Order Insurance</a>
                                            </PreIcon>
                                        }
                                    </CardItem>

                                    {hasTitleInsurance &&
                                        <div className="mt-1">
                                            <Badge pill bg="info">{insuranceInfo.status || "Not submitted"}</Badge>
                                        </div>
                                    }
                                </Col>}

                                {showFireInsurance &&
                                <Col className={styles.glow} onClick={() => navigate("Fire Insurance")}>
                                    <CardItem label="Fire Insurance">
                                        <LField text={DataReaderObj.getFireInsuranceCompany()} txtF="Add Insurance"></LField>
                                    </CardItem>
                                </Col>}
                            </Row>

                            {showExtraProperties && 
                                <Row className="mb-3">
                                    <Col className={styles.glow} onClick={() => navPage("Extra Properties", {tabName: "Basic", tabNumber: 1, autoClick: "ExtraProperties"})}>
                                        <CardItem label="Extra Properties">
                                            {extraProperties.map((addr) => <div style={{ display: 'block'}}>{addr}</div>)}
                                            <a>Add Extra Property</a>
                                        </CardItem>
                                    </Col>
                                </Row>
                            }

                            {showTaxLinks &&
                                <Row className="mb-3">
                                    <Col>
                                        <Form.Label>Tax Search</Form.Label>
                                        <Dropdown title="Tax" align={'start'}>
                                            <Dropdown.Toggle as={TaxToggle}/>
                                            <Dropdown.Menu>
                                                {taxLinks.map((item) => 
                                                    <DropdownItem key={item.label} target="_blank" href={item.navigate.url}>{item.label}<i className="fa-regular fa-arrow-up-right"/></DropdownItem>
                                                )}
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </Col>
                                </Row>
                            }
                        </Container>
                    </Card.Body>
                </>
            }
        </Card>
    }

    const BuyerCard = () => {
        // file side card
        let persons: PurchaserSummaryType[] = [], pstr: string, tabName: PopupPageId, listID: string;
        switch(PSM) {
            case "P":
                persons = DataReaderObj.getPurchaserSummary();
                pstr = "Buyer";
                listID = "Buyer";
                tabName = PopupPageId.Transferee;
                break;
            case "S":
                persons = DataReaderObj.getVendorSummary();
                listID = "Seller";
                pstr = "Seller";
                tabName = PopupPageId.Transferor;
                break;
            case "M":
                persons = DataReaderObj.getMortgagerSummary();
                listID = "Buyer";
                pstr = "Borrower";
                tabName = PopupPageId.Transferee;
                break;
        }
        const addOptions = detailsList.find(node => node.id === listID)?.addOptions || [];
        const addMenuNav = (navObject: NavObjectType) => {
            let controller = navObject.controller;
            controller.tabNumber = persons.length + 1
            navPage(navObject.label, controller);
        }
        return <Card>
            {persons.length > 0 && <Card.Header>
                <Card.Title>
                    <i className='fa-duotone fa-user-plus'></i>
                    {`${pstr}(s)`}
                </Card.Title>
                
                <Button variant="outline-primary" onClick={() => navPage("Client Intake Information", {tabName: "Client Portal"}, "CLIENTPORTAL")}>
                    <i className="fak fa-connect-solid fa-action"></i>
                    ClientConnect
                </Button>

                <OptionsDropdown options={addOptions} nav={addMenuNav}/>
            </Card.Header>}

            {!(persons.length) ? 
                <EmptyField
                    icon="fa-user-slash"
                    txt={`${pstr}s`}
                    linkText={`Add ${pstr}`} 
                    navFunc={() => navPage("", {tabName: tabName, tabNumber: 1})}
                />
                :
                <>
                    <Card.Body>
                        {persons.map((val, index) => {
                            return( 
                                <Row key={index} className={"gx-1 " + styles.glow}>
                                    <Col onClick={() => {navPage(val.name, {tabName: tabName, tabNumber: index + 1})}}>
                                        <span className={styles.editIcon}>
                                            {val.name}
                                            {val.mainContact == '1' && 
                                                <OverlayTrigger placement="bottom-end" overlay={<Tooltip>Main Contact</Tooltip>}>
                                                    <span className="required">*</span>
                                                </OverlayTrigger>
                                            }
                                        </span>
                                    </Col>
                                    {(val.verifications.singleProcess >= 1 || val.verifications.dualProcess >= 2) &&
                                    <Col xs={"auto"}>
                                        <OverlayTrigger placement="bottom-end" overlay={<Tooltip>ID Verified</Tooltip>}>
                                            <i className="fa-solid fa-circle-check status-success"></i>
                                        </OverlayTrigger>
                                    </Col>
                                    }
                                    <Col xs={"auto"}>
                                        <OverlayTrigger placement="bottom-end" overlay={<Tooltip>{formatPhoneNumber(val.phone) || "No Phone Number"}</Tooltip>}>
                                            <i className={`fa-duotone fa-phone ${!val.phone ? "status-secondary-light" : ""} ${styles.contactButton}`}></i>
                                        </OverlayTrigger>
                                    </Col>

                                    <Col xs={"auto"}>
                                        <OverlayTrigger placement="bottom-end" overlay={<Tooltip>{val.email || "No Email"}</Tooltip>}>
                                            <i className={`fa-duotone fa-envelope ${!val.email ? "status-secondary-light" : ""} ${styles.contactButton}`}></i>
                                        </OverlayTrigger>
                                    </Col>
                                </Row>
                            )
                        })}
                    </Card.Body>
  
                    <Card.Footer>
                        {provinceCode === "ON" &&
                        <>
                        <Button variant="link-text" onClick={() => navigate("Writ Search", "Due Diligence")}>
                            <i className="fa-regular fa-user-magnifying-glass"></i>
                            Writ Search
                        </Button>
                        <Button variant="link-text" onClick={() => navigate("Similar Names", "Due Diligence")}>
                            <i className="fa-regular fa-input-text"></i>
                            Similar Names
                        </Button>
                        </>}
                    </Card.Footer>
                </>
            }
        </Card>
    }

    const SellerCard = () => {
        // other side card
        let persons: PurchaserSummaryType[] = [], pstr: string, tabName: PopupPageId, listID: string;
        let lawyerName=DataReaderObj.getOtherSideLawyerName() || "";
        let lawyerPhone=DataReaderObj.getOtherSideLawyerField('DirectPhone');
        let lawyerEmail=DataReaderObj.getOtherSideLawyerField('FirmEmail');
        let contacts = DataReaderObj.getContactPersons();
        switch(PSM){
            case "P":
                persons = DataReaderObj.getVendorSummary();
                listID = "Seller";
                pstr = "Seller";
                tabName = PopupPageId.Transferor;
                break;
            case "S":
                persons = DataReaderObj.getPurchaserSummary();
                listID = "Buyer";
                pstr = "Buyer";
                tabName = PopupPageId.Transferee;
                break;
            case "M":
                persons = DataReaderObj.getMortgagerSummary();
                listID = "Buyer";
                pstr = "Borrower";
                tabName = PopupPageId.Transferee;
                break;
        }
        const addOptions = detailsList.find(node => node.id === listID)?.addOptions || [];

        const addMenuNav = (navObject: NavObjectType) => {
            let controller = navObject.controller;
            controller.tabNumber = persons.length + 1
            navPage(navObject.label, controller);
        }
        const addSeller = () => navPage("", {tabName: tabName, tabNumber: 1});
        const addLawyer = PSM === "M"? () => navigate("Basic") : () => {
            navPage("Solicitor", {tabName: PopupPageId.Solicitor}); 
            dispatch(setLawyerListState(lawyerName === ""));
        };
        return (
            <Card>
                {(persons.length > 0 || lawyerName > "") &&
                    <Card.Header>
                        <Card.Title>
                            <i className='fa-duotone fa-user-minus'></i>
                            {`${pstr} Side`}
                        </Card.Title>

                        <OptionsDropdown options={addOptions} nav={addMenuNav}/>
                    </Card.Header>
                }

                {!(persons.length || lawyerName) ?
                    <EmptyField icon="fa-user-slash" txt={`${pstr} Side`} linkText={`Add ${pstr}`} navFunc={addSeller}>
                        <a className={styles.underline} onClick={addLawyer}>Add {pstr}'s Lawyer</a>
                    </EmptyField>
                    :
                    <>
                        <Card.Body>

                            {lawyerName > ""?
                                <Row className={"gx-2 " + styles.glow} onClick={addLawyer}>
                                    <Col>
                                        <span className={styles.editIcon}>
                                            {CheckEmpty(lawyerName)}
                                            <Badge pill bg="info" style={{marginLeft: '8px'}}>Lawyer</Badge>
                                        </span>
                                    </Col>

                                    {lawyerPhone > "" &&
                                        <Col xs={"auto"}>
                                            <OverlayTrigger placement="bottom-end" overlay={<Tooltip>{`${formatPhoneNumber(lawyerPhone)}`}</Tooltip>}>
                                                <i className={`fa-duotone fa-phone ${!lawyerPhone ? "status-secondary-light" : ""} ${styles.contactButton}`}></i>
                                            </OverlayTrigger>
                                        </Col>
                                    }

                                    {lawyerEmail > "" &&
                                        <Col xs={"auto"}>
                                            <OverlayTrigger placement="bottom-end" overlay={<Tooltip>{lawyerEmail}</Tooltip>}>
                                                <i className={`fa-duotone fa-envelope ${!lawyerEmail ? "status-secondary-light" : ""} ${styles.contactButton}`}></i>
                                            </OverlayTrigger>
                                        </Col>
                                    }
                                </Row>
                                :
                                <PreIcon cond={false}><a className={styles.underline} onClick={addLawyer}>Add Lawyer</a></PreIcon>
                            }
                            {contacts.length > 0 && contacts.map((val, index) => {
                                return <Row key={index} className={"gx-2 " + styles.glow} onClick={addLawyer}>
                                    <Col>
                                        <span className={styles.editIcon}>
                                            {CheckEmpty(val.name)}
                                            <Badge pill bg="secondary" style={{marginLeft: '8px'}}>Firm Contact</Badge>
                                        </span>
                                    </Col>

                                    {val.phone > "" &&
                                        <Col xs={"auto"}>
                                            <OverlayTrigger placement="bottom-end" overlay={<Tooltip>{`${formatPhoneNumber(val.phone)}`}</Tooltip>}>
                                                <i className={`fa-duotone fa-phone ${!val.phone ? "status-secondary-light" : ""} ${styles.contactButton}`}></i>
                                            </OverlayTrigger>
                                        </Col>
                                    }

                                    {val.email > "" &&
                                        <Col xs={"auto"}>
                                            <OverlayTrigger placement="bottom-end" overlay={<Tooltip>{val.email}</Tooltip>}>
                                                <i className={`fa-duotone fa-envelope ${!val.email ? "status-secondary-light" : ""} ${styles.contactButton}`}></i>
                                            </OverlayTrigger>
                                        </Col>
                                    }
                                </Row>
                            })}

                            <hr />

                            {persons.length? persons.map((val, index)=> {
                                return( 
                                    <Row key={index} className={"gx-2 " + styles.glow} onClick={() => {navPage(val.name, {tabName: tabName, tabNumber: index + 1})}}>
                                        <Col>
                                            <span className={styles.editIcon}>{val.name}</span>
                                        </Col>
                                    </Row>
                                )
                            }):
                            <PreIcon cond={false}><a className={styles.underline} onClick={addSeller}>{`Add ${pstr}`}</a></PreIcon>}
                        </Card.Body>

                        <Card.Footer>
                            {provinceCode === "ON" &&
                                <>
                                    <Button variant="link-text" onClick={() => navigate("Writ Search", "Due Diligence")}>
                                        <i className="fa-regular fa-user-magnifying-glass"></i>
                                        Writ Search
                                    </Button>
                                    <Button variant="link-text" onClick={() => navigate("Similar Names", "Due Diligence")}>
                                        <i className="fa-regular fa-input-text"></i>
                                        Similar Names
                                    </Button>
                                </>
                            }
                        </Card.Footer>
                    </>
                }
            </Card>
        );
    }
    const MortgageCard = () => {
        const mortgages = DataReaderObj.getAllMortgageSummary();
        const mortgageNav = (mortgage: MortgageSummaryType, index: number) => {
            let navObj: NavObjectType = {
                component: "main",
                url: "lfmaster.asp",
                params: {
                    "tb": "TPAppXML",
                    "formasp": "purfileedit",
                    "id": appID,
                    "PSM": PSM,
                },
                controller: ["Discharge", "Assumed"].includes(mortgage.type)? 
                {
                    tabName:  "Issues",
                    tabNumber: 0,
                } : {
                    tabName: "Mortgages",
                    tabNumber: index,
                },
                action: ["Discharge", "Assumed"].includes(mortgage.type)?"ReactShell.ReactEditExistingMortgage":undefined,
                actionParam: ["Discharge", "Assumed"].includes(mortgage.type)?mortgage.index:"",
                label: mortgage.lender,
            }
            return () => {navToDetails(navObj)}
        }
        // not adding vendor take back rn it doesnt work properly and we need to fix the live behaviour before we try to modify it for summary
        const addOptions = detailsList.find(node => node.label === "Mortgage")?.addOptions?.filter(node => 
            (!node.appCode || node.appCode.includes(appCode)) && (!node.PSM || node.PSM.includes(PSM))).filter(node => node.label !== "Vendor Take Back") || [];
        const addOptionsNav = (navObj: NavObjectType) => {
            if(navObj.action){
                navObj.url = "lfmaster.asp"
                navObj.params = {
                    "tb": "TPAppXML",
                    "formasp": "purfileedit",
                    "id": appID,
                    "PSM": PSM,
                };
            }
            nav(navObj);
        }
        return <Card>
            {!(mortgages.length) ?
                <EmptyField 
                    icon="fa-shop-slash" 
                    txt="Mortgages" 
                    linkText=''  
                    navFunc={() => {return}}
                >
                    <Button variant="warning" onClick={() => {nav({
                        component: "main",
                        controller: {
                            onClickDispatch: "MortgageDropbox",
                        },
                    })}}>
                        <i className="fa-solid fa-sparkles"></i>Import with RenAI
                    </Button>
                    {PSM !== "S" && <Button variant="outline-primary" onClick={() => nav({
                        component: "main",
                        popup: true,
                        isModal: true,
                        reactModalFrame: "mortimp",
                    })}>
                        <i className="fa-regular fa-inbox"></i>
                        Inbox & LLC
                    </Button>}
                    <a className={styles.underline + " mt-2"} style={{display: "flex", alignItems: "center", justifyContent: "center"}}><OptionsDropdown options={addOptions} nav={addOptionsNav} text={"Add a Mortgage"}/></a>
                </EmptyField>
                :
                <>
                    <Card.Header>
                        <Card.Title>
                            <i className='fa-duotone fa-building-columns'></i>
                            Mortgage
                        </Card.Title>
                        {PSM !== "S" && 
                            <Button variant="outline-primary" onClick={() => nav({
                                    component: "main",
                                    popup: true,
                                    isModal: true,
                                    reactModalFrame: "mortimp",
                                })}>
                                <i className="fa-regular fa-inbox"></i>
                                Inbox & LLC
                            </Button>
                        }
                        <Button variant="outline-primary" onClick={() => {nav({
                            component: "main",
                            controller: {
                                onClickDispatch: "MortgageDropbox",
                            },
                        })}}>
                            <i className="fa-solid fa-sparkles"></i>
                            Import with RenAI
                        </Button>
                        <OptionsDropdown options={addOptions} nav={addOptionsNav}/>
                    </Card.Header>

                    <Card.Body>
                        <Row className="gx-2">
                            <Col xs={6}><Form.Label>Lender</Form.Label></Col>
                            <Col xs={2}><Form.Label>Reference No.</Form.Label></Col>
                            <Col xs={2} className="text-end"><Form.Label>Principal</Form.Label></Col>
                            <Col xs={2} className="text-end"><Form.Label>Payout/Advance</Form.Label></Col>
                        </Row>

                        {mortgages.map((val, index) =>
                            <Row key={index} className={"gx-2 " + styles.glow} onClick={mortgageNav(val, index + 1)}>
                                <Col>
                                    <div style={{display: "flex", alignItems:'center', justifyContent:"left"}} className={styles.editIcon}>
                                        <span>{val.lender}</span> 
                                        {val.type!=null?<Badge pill bg="info" text="dark" style={{marginLeft: "5px"}}>{val.type}</Badge>:<></>}
                                        {val.position && <Badge pill bg="info" text="dark">{val.position}</Badge>}
                                    </div>
                                </Col>
                                <Col xs={2}>{val.ref > '' ? val.ref : <i>not added</i>}</Col>
                                <Col xs={2} className="text-end">{formatMoney(val.amount)}</Col>
                                <Col xs={2} className="text-end">{formatMoney(val.advance)}</Col>
                            </Row>
                        )}
                    </Card.Body>
                </>
            }
        </Card>
    }

    const FinancialCard = () => {
        const adjustmentDate=DataReaderObj.getAdjustmentDate();
        const financialInfo=DataReaderObj.getFinancialInformation();
        const hasAdjustment = (adjustmentDate !== null && adjustmentDate > "") || PSM === "M";
        const price=DataReaderObj.getContractPrice();
        const deposit = DataReaderObj.getDepositTotal();
        const navContract = () => navigate("Contract");
        let priceHeader = "Contract Price";

        switch(provinceCode){
            case "BC":
                priceHeader = PSM === "S"? "Sale Price":"Purchase Price";
                break;
            default:
        }

        const renderFinancialRow = (rowConfig: {
            mainLabel?: string;
            subLabel?: string;
            navLabel?: string;
            navTabName?: string;
            navTabNumber?: number;
            value?: ReactNode;
            className?: string;
            colProps?: any;
        }) => {
            
            const handleClick = () => {
                if (rowConfig.navLabel && rowConfig.navTabName !== undefined) {
                    navPage(rowConfig.navLabel, {
                        tabName: rowConfig.navTabName as any,
                        tabNumber: rowConfig.navTabNumber || 0,
                    });
                }
            };

            return (
                <Col {...(rowConfig.colProps || {})} className={"mb-3 " + styles.glow} xs="6" onClick={handleClick}>
                    <Form.Label>
                        {rowConfig.mainLabel}
                    </Form.Label>

                    <span className={styles.editIcon}>{rowConfig.value > '' ? rowConfig.value : "0.00"}</span>
                    
                    {rowConfig.subLabel && (
                        <div>
                            <small>{rowConfig.subLabel}</small>
                        </div>
                    )}
                </Col>
            );
        };

        const getFormattedValue = (dataType: "money" | "date" | undefined, dataValue: (() => string | number) | undefined): ReactNode => {
            if (!dataType || !dataValue) return undefined;
            const rawValue = dataValue();
            if (dataType === "money") {
                return formatMoney(rawValue);
            } else if (dataType === "date") {
                return formatDate(rawValue as string);
            }
            return undefined;
        };

        // TODO: migrate this financialRowsConfig to a separate json file before release
        const financialRowsConfig = {
            ON: {
                P: {
                    adjustmentDate: {
                        mainLabel: "Adjustment Date",
                        navLabel: "Statement of Adjustments",
                        navTabName: "Financial",
                        navTabNumber: 0,
                        dataType: "date",
                        dataValue: () => adjustmentDate,
                    },
                    accountToClient: {
                        mainLabel: "Account to Client",
                        navLabel: "Account to Client",
                        navTabName: "Financial",
                        navTabNumber: 2,
                        dataType: "money",
                        dataValue: () => financialInfo.account.owing,
                    },
                    statementOfAdjustments: {
                        mainLabel: "Statement of Adjustments",
                        subLabel: "Balance Due on Closing",
                        navLabel: "Statement of Adjustments",
                        navTabName: "Financial",
                        navTabNumber: 0,
                        dataType: "money",
                        dataValue: () => financialInfo.adjustment.bdoc,
                    },
                    trustStatement: {
                        mainLabel: "Trust Statement",
                        subLabel: financialInfo.trust.owingFrom > 0 ? "Owing from Client" : "Owing to Client",
                        navLabel: "Trust Statement",
                        navTabName: "Financial",
                        navTabNumber: 3,
                        dataType: "money",
                        dataValue: () => financialInfo.trust.owingFrom > 0 ? financialInfo.trust.owingFrom : financialInfo.trust.owingTo,
                    },
                },
                S: {
                    adjustmentDate: {
                        mainLabel: "Adjustment Date",
                        navLabel: "Statement of Adjustments",
                        navTabName: "Financial",
                        navTabNumber: 0,
                        dataType: "date",
                        dataValue: () => adjustmentDate,
                    },
                    accountToClient: {
                        mainLabel: "Account to Client",
                        navLabel: "Account to Client",
                        navTabName: "Financial",
                        navTabNumber: 2,
                        dataType: "money",
                        dataValue: () => financialInfo.account.owing,
                    },
                    statementOfAdjustments: {
                        mainLabel: "Statement of Adjustments",
                        subLabel: "Balance Due on Closing",
                        navLabel: "Statement of Adjustments",
                        navTabName: "Financial",
                        navTabNumber: 0,
                        dataType: "money",
                        dataValue: () => financialInfo.adjustment.bdoc,
                    },
                    trustStatement: {
                        mainLabel: "Trust Statement",
                        subLabel: financialInfo.trust.owingFrom > 0 ? "Owing from Client" : "Owing to Client",
                        navLabel: "Trust Statement",
                        navTabName: "Financial",
                        navTabNumber: 3,
                        dataType: "money",
                        dataValue: () => financialInfo.trust.owingFrom > 0 ? financialInfo.trust.owingFrom : financialInfo.trust.owingTo,
                    },
                },
                M: {
                    accountToClient: {
                        mainLabel: "Account to Client",
                        navLabel: "Account to Client",
                        navTabName: "Financial",
                        navTabNumber: 2,
                        dataType: "money",
                        dataValue: () => financialInfo.account.owing,
                    },
                    trustStatement: {
                        mainLabel: "Trust Statement",
                        subLabel: financialInfo.trust.owingFrom > 0 ? "Owing from Client" : "Owing to Client",
                        navLabel: "Trust Statement",
                        navTabName: "Financial",
                        navTabNumber: 3,
                        dataType: "money",
                        dataValue: () => financialInfo.trust.owingFrom > 0 ? financialInfo.trust.owingFrom : financialInfo.trust.owingTo,
                    },
                },
            },
            AB: {
                P: {
                    adjustmentDate: {
                        mainLabel: "Adjustment Date",
                        navLabel: "Statement of Adjustments",
                        navTabName: "Financial",
                        navTabNumber: 0,
                        dataType: "date",
                        dataValue: () => adjustmentDate,
                    },
                    accountToClient: {
                        mainLabel: "Account to Client",
                        navLabel: "Account to Client",
                        navTabName: "Financial",
                        navTabNumber: 1,
                        dataType: "money",
                        dataValue: () => financialInfo.account.owing,
                    },
                    statementOfAdjustments: {
                        mainLabel: "Statement of Adjustments",
                        subLabel: "Cash to Close",
                        navLabel: "Statement of Adjustments",
                        navTabName: "Financial",
                        navTabNumber: 0,
                        dataType: "money",
                        dataValue: () => financialInfo.adjustment.bdoc,
                    },
                    trustStatement: {
                        mainLabel: "Trust Statement",
                        subLabel: financialInfo.trust.owingFrom > 0 ? "Owing from Client" : "Owing to Client",
                        navLabel: "Trust Statement",
                        navTabName: "Financial",
                        navTabNumber: 2,
                        dataType: "money",
                        dataValue: () => financialInfo.trust.owingFrom > 0 ? financialInfo.trust.owingFrom : financialInfo.trust.owingTo,
                    },
                },
                S: {
                    adjustmentDate: {
                        mainLabel: "Adjustment Date",
                        navLabel: "Statement of Adjustments",
                        navTabName: "Financial",
                        navTabNumber: 0,
                        dataType: "date",
                        dataValue: () => adjustmentDate,
                    },
                    accountToClient: {
                        mainLabel: "Account to Client",
                        navLabel: "Account to Client",
                        navTabName: "Financial",
                        navTabNumber: 1,
                        dataType: "money",
                        dataValue: () => financialInfo.account.owing,
                    },
                    statementOfAdjustments: {
                        mainLabel: "Statement of Adjustments",
                        subLabel: "Cash to Close",
                        navLabel: "Statement of Adjustments",
                        navTabName: "Financial",
                        navTabNumber: 0,
                        dataType: "money",
                        dataValue: () => financialInfo.adjustment.bdoc,
                    },
                    trustStatement: {
                        mainLabel: "Trust Statement",
                        subLabel: financialInfo.trust.owingFrom > 0 ? "Owing from Client" : "Owing to Client",
                        navLabel: "Trust Statement",
                        navTabName: "Financial",
                        navTabNumber: 2,
                        dataType: "money",
                        dataValue: () => financialInfo.trust.owingFrom > 0 ? financialInfo.trust.owingFrom : financialInfo.trust.owingTo,
                    },
                },
                M: {
                    accountToClient: {
                        mainLabel: "Account to Client",
                        navLabel: "Account to Client",
                        navTabName: "Financial",
                        navTabNumber: 1,
                        dataType: "money",
                        dataValue: () => financialInfo.account.owing,
                    },
                    trustStatement: {
                        mainLabel: "Trust Statement",
                        subLabel: financialInfo.trust.owingFrom > 0 ? "Owing from Client" : "Owing to Client",
                        navLabel: "Trust Statement",
                        navTabName: "Financial",
                        navTabNumber: 2,
                        dataType: "money",
                        dataValue: () => financialInfo.trust.owingFrom > 0 ? financialInfo.trust.owingFrom : financialInfo.trust.owingTo,
                    },
                },
            },
            BC: {
                P: {
                    adjustmentDate: {
                        mainLabel: "Adjustment Date",
                        navLabel: "Vendor Adjustments",
                        navTabName: "Financial",
                        navTabNumber: 0,
                        dataType: "date",
                        dataValue: () => adjustmentDate,
                    },
                    accountToClient: {
                        mainLabel: "Account to Client",
                        navLabel: "Account to Client",
                        navTabName: "Financial",
                        navTabNumber: 3,
                        dataType: "money",
                        dataValue: () => financialInfo.account.owing,
                    },
                    saleProceedsToSeller: {
                        mainLabel: "Sale Proceeds to Seller",
                        subLabel: "Balance due to Vendor's Lawyer/Notary on Completion",
                        navLabel: "Vendor Adjustments",
                        navTabName: "Financial",
                        navTabNumber: 0,
                        dataType: "money",
                        dataValue: () => financialInfo.adjustment.bdoc,
                    },
                    trustStatement: {
                        mainLabel: "Trust In/Out",
                        subLabel: financialInfo.trust.owingFrom > 0 ? "Owing from Client" : "Owing to Client",
                        navLabel: "Trust In/Out",
                        navTabName: "Financial",
                        navTabNumber: 2,
                        dataType: "money",
                        dataValue: () => financialInfo.trust.owingFrom > 0 ? financialInfo.trust.owingFrom : financialInfo.trust.owingTo,
                    },
                    balanceToClose: {
                        mainLabel: "Balance to Close",
                        subLabel: "Balance required to close",
                        navLabel: "Purchaser Adjustments",
                        navTabName: "Financial",
                        navTabNumber: 1,
                        dataType: "money",
                        dataValue: () => financialInfo.trust.estimatedOwingFrom > 0 ? financialInfo.trust.estimatedOwingFrom : financialInfo.trust.estimatedOwingTo,
                    },
                },
                S: {
                    adjustmentDate: {
                        mainLabel: "Adjustment Date",
                        navLabel: "Vendor Adjustments",
                        navTabName: "Financial",
                        navTabNumber: 0,
                        dataType: "date",
                        dataValue: () => adjustmentDate,
                    },
                    accountToClient: {
                        mainLabel: "Account to Client",
                        navLabel: "Account to Client",
                        navTabName: "Financial",
                        navTabNumber: 3,
                        dataType: "money",
                        dataValue: () => financialInfo.account.owing,
                    },
                    netSaleProceeds: {
                        mainLabel: "Net Sale Proceeds",
                        navLabel: "Trust In/Out",
                        navTabName: "Financial",
                        navTabNumber: 2,
                        dataType: "money",
                        dataValue: () => financialInfo.trust.netSaleProceeds ?? 0,
                    },
                    balanceSaleProceeds: {
                        mainLabel: "Balance Sale Proceeds",
                        navLabel: "Trust In/Out",
                        navTabName: "Financial",
                        navTabNumber: 2,
                        dataType: "money",
                        dataValue: () => financialInfo.trust.balanceSaleProceeds ?? 0,
                    },
                    trustStatement: {
                        mainLabel: "Trust In/Out",
                        subLabel: financialInfo.trust.owingFrom > 0 ? "Owing from Client" : "Owing to Client",
                        navLabel: "Trust In/Out",
                        navTabName: "Financial",
                        navTabNumber: 2,
                        dataType: "money",
                        dataValue: () => financialInfo.trust.owingFrom > 0 ? financialInfo.trust.owingFrom : financialInfo.trust.owingTo,
                    },
                },
                M: {
                    accountToClient: {
                        mainLabel: "Account to Client",
                        navLabel: "Account to Client",
                        navTabName: "Financial",
                        navTabNumber: 3,
                        dataType: "money",
                        dataValue: () => financialInfo.account.owing,
                    },
                    trustStatement: {
                        mainLabel: "Trust In/Out",
                        subLabel: financialInfo.trust.owingFrom > 0 ? "Owing from Client" : "Owing to Client",
                        navLabel: "Trust In/Out",
                        navTabName: "Financial",
                        navTabNumber: 2,
                        dataType: "money",
                        dataValue: () => financialInfo.trust.owingFrom > 0 ? financialInfo.trust.owingFrom : financialInfo.trust.owingTo,
                    },
                },
            },
            MB: {
                P: {
                    adjustmentDate: {
                        mainLabel: "Adjustment Date",
                        navLabel: "Statement of Adjustments",
                        navTabName: "Financial",
                        navTabNumber: 0,
                        dataType: "date",
                        dataValue: () => adjustmentDate,
                    },
                    accountToClient: {
                        mainLabel: "Account to Client",
                        navLabel: "Account to Client",
                        navTabName: "Financial",
                        navTabNumber: 2,
                        dataType: "money",
                        dataValue: () => financialInfo.account.owing,
                    },
                    statementOfAdjustments: {
                        mainLabel: "Statement of Adjustments",
                        subLabel: "Balance Due on Closing",
                        navLabel: "Statement of Adjustments",
                        navTabName: "Financial",
                        navTabNumber: 0,
                        dataType: "money",
                        dataValue: () => financialInfo.adjustment.bdoc,
                    },
                    trustStatement: {
                        mainLabel: "Trust Statement",
                        subLabel: financialInfo.trust.owingFrom > 0 ? "Owing from Client" : "Owing to Client",
                        navLabel: "Trust Statement",
                        navTabName: "Financial",
                        navTabNumber: 4,
                        dataType: "money",
                        dataValue: () => financialInfo.trust.owingFrom > 0 ? financialInfo.trust.owingFrom : financialInfo.trust.owingTo,
                    },
                    estimatedBalanceToClose: {
                        mainLabel: "Estimated Balance to Close",
                        navLabel: "Estimated Closing Costs",
                        navTabName: "Financial",
                        navTabNumber: 1,
                        dataType: "money",
                        dataValue: () => financialInfo.trust.estimatedOwingFrom > 0 ? financialInfo.trust.estimatedOwingFrom : financialInfo.trust.estimatedOwingTo,
                    },
                },
                S: {
                    adjustmentDate: {
                        mainLabel: "Adjustment Date",
                        navLabel: "Statement of Adjustments",
                        navTabName: "Financial",
                        navTabNumber: 0,
                        dataType: "date",
                        dataValue: () => adjustmentDate,
                    },
                    accountToClient: {
                        mainLabel: "Account to Client",
                        navLabel: "Account to Client",
                        navTabName: "Financial",
                        navTabNumber: 2,
                        dataType: "money",
                        dataValue: () => financialInfo.account.owing,
                    },
                    statementOfAdjustments: {
                        mainLabel: "Statement of Adjustments",
                        subLabel: "Balance Due on Closing",
                        navLabel: "Statement of Adjustments",
                        navTabName: "Financial",
                        navTabNumber: 0,
                        dataType: "money",
                        dataValue: () => financialInfo.adjustment.bdoc,
                    },
                    trustStatement: {
                        mainLabel: "Trust Statement",
                        subLabel: financialInfo.trust.owingFrom > 0 ? "Owing from Client" : "Owing to Client",
                        navLabel: "Trust Statement",
                        navTabName: "Financial",
                        navTabNumber: 4,
                        dataType: "money",
                        dataValue: () => financialInfo.trust.owingFrom > 0 ? financialInfo.trust.owingFrom : financialInfo.trust.owingTo,
                    },
                },
                M: {
                    accountToClient: {
                        mainLabel: "Account to Client",
                        navLabel: "Account to Client",
                        navTabName: "Financial",
                        navTabNumber: 2,
                        dataType: "money",
                        dataValue: () => financialInfo.account.owing,
                    },
                    trustStatement: {
                        mainLabel: "Trust Statement",
                        subLabel: financialInfo.trust.owingFrom > 0 ? "Owing from Client" : "Owing to Client",
                        navLabel: "Trust Statement",
                        navTabName: "Financial",
                        navTabNumber: 4,
                        dataType: "money",
                        dataValue: () => financialInfo.trust.owingFrom > 0 ? financialInfo.trust.owingFrom : financialInfo.trust.owingTo,
                    },
                },
            },
            SK: {
                P: {
                    adjustmentDate: {
                        mainLabel: "Adjustment Date",
                        navLabel: "Statement of Adjustments",
                        navTabName: "Financial",
                        navTabNumber: 0,
                        dataType: "date",
                        dataValue: () => adjustmentDate,
                    },
                    accountToClient: {
                        mainLabel: "Account to Client",
                        navLabel: "Account to Client",
                        navTabName: "Financial",
                        navTabNumber: 2,
                        dataType: "money",
                        dataValue: () => financialInfo.account.owing,
                    },
                    statementOfAdjustments: {
                        mainLabel: "Statement of Adjustments",
                        subLabel: "Cash to Close",
                        navLabel: "Statement of Adjustments",
                        navTabName: "Financial",
                        navTabNumber: 0,
                        dataType: "money",
                        dataValue: () => financialInfo.adjustment.bdoc,
                    },
                    trustStatement: {
                        mainLabel: "Purchaser's Adjustment",
                        subLabel: financialInfo.trust.owingFrom > 0 ? "Owing from Client" : "Owing to Client",
                        navLabel: "Purchaser's Adjustments",
                        navTabName: "Financial",
                        navTabNumber: 4,
                        dataType: "money",
                        dataValue: () => financialInfo.trust.owingFrom > 0 ? financialInfo.trust.owingFrom : financialInfo.trust.owingTo,
                    },
                    estimatedPurchaserAdjustment: {
                        mainLabel: "Estimated Purchaser's Adjustment",
                        subLabel: financialInfo.trust.estimatedOwingFrom > 0 ? "Balance Required to Close" : "Paid To Client",
                        navLabel: "Estimated Purchaser's Adjustments",
                        navTabName: "Financial",
                        navTabNumber: 3,
                        dataType: "money",
                        dataValue: () => financialInfo.trust.estimatedOwingFrom > 0 ? financialInfo.trust.estimatedOwingFrom : financialInfo.trust.estimatedOwingTo,
                    },
                },
                S: {
                    adjustmentDate: {
                        mainLabel: "Adjustment Date",
                        navLabel: "Statement of Adjustments",
                        navTabName: "Financial",
                        navTabNumber: 0,
                        dataType: "date",
                        dataValue: () => adjustmentDate,
                    },
                    accountToClient: {
                        mainLabel: "Account to Client",
                        navLabel: "Account to Client",
                        navTabName: "Financial",
                        navTabNumber: 2,
                        dataType: "money",
                        dataValue: () => financialInfo.account.owing,
                    },
                    statementOfAdjustments: {
                        mainLabel: "Statement of Adjustments",
                        subLabel: "Cash to Close",
                        navLabel: "Statement of Adjustments",
                        navTabName: "Financial",
                        navTabNumber: 0,
                        dataType: "money",
                        dataValue: () => financialInfo.adjustment.bdoc,
                    },
                    trustStatement: {
                        mainLabel: "Vendor's Adjustment",
                        subLabel: financialInfo.trust.owingFrom > 0 ? "Owing from Client" : "Owing to Client",
                        navLabel: "Vendor's Adjustments",
                        navTabName: "Financial",
                        navTabNumber: 4,
                        dataType: "money",
                        dataValue: () => financialInfo.trust.owingFrom > 0 ? financialInfo.trust.owingFrom : financialInfo.trust.owingTo,
                    },
                    estimatedVendorAdjustment: {
                        mainLabel: "Estimated Vendor's Adjustment",
                        subLabel: financialInfo.trust.estimatedOwingTo > 0 ? "Paid To Client" : "Balance Required to Close",
                        navLabel: "Estimated Vendor's Adjustments",
                        navTabName: "Financial",
                        navTabNumber: 3,
                        dataType: "money",
                        dataValue: () => financialInfo.trust.estimatedOwingTo > 0 ? financialInfo.trust.estimatedOwingTo : financialInfo.trust.estimatedOwingFrom,
                    },
                },
                M: {
                    accountToClient: {
                        mainLabel: "Account to Client",
                        navLabel: "Account to Client",
                        navTabName: "Financial",
                        navTabNumber: 2,
                        dataType: "money",
                        dataValue: () => financialInfo.account.owing,
                    },
                    trustStatement: {
                        mainLabel: "Mortgagor's Adjustment",
                        subLabel: financialInfo.trust.owingFrom > 0 ? "Owing from Client" : "Owing to Client",
                        navLabel: "Mortgagor's Adjustments",
                        navTabName: "Financial",
                        navTabNumber: 4,
                        dataType: "money",
                        dataValue: () => financialInfo.trust.owingFrom > 0 ? financialInfo.trust.owingFrom : financialInfo.trust.owingTo,
                    },
                },
            },
        };

        const config = financialRowsConfig[provinceCode]?.[PSM] || financialRowsConfig.ON[PSM] || financialRowsConfig.ON.P;
        
        return <Card>
            {!hasAdjustment ?
                <EmptyField 
                    icon="fa-circle-dollar" 
                    txt="Financial" 
                    linkText={`Add ${["SK", "BC", "MB"].includes(provinceCode)?"Possession":"Closing"} Date`}
                    navFunc={() => navPage("Basic", {tabName: "Basic", tabNumber: 0})}
                />
            :
                <>
                    <Card.Header>
                        <Card.Title>
                            <i className='fa-duotone fa-circle-dollar'></i>
                            Financial
                        </Card.Title>
                    </Card.Header>
                    <Card.Body>
                        <Container>
                            {PSM === "M" || <Row className="mb-3">


                                <Col className={styles.glow} onClick={navContract}>
                                    <CardItem label={priceHeader}>
                                        <LField text={formatMoney(price)} txtF='Add Price'></LField>
                                    </CardItem>
                                </Col>

                                <Col className={styles.glow} onClick={navContract}>
                                    <CardItem label="Total Deposits">
                                        <LField text={formatMoney(deposit)} txtF='Add Deposits'></LField>
                                    </CardItem>
                                </Col>

                                
                            </Row>}
    
                            {PSM === "M" || (
                                <Row className="mb-3">
                                    <Col>
                                        <hr />
                                    </Col>
                                </Row>
                            )}

                            <Row>
                                {Object.keys(config)
                                    .map((rowKey, index, array) => {
                                        const rowConfig = config[rowKey as keyof typeof config];
                                        if (!rowConfig || typeof rowConfig !== 'object' || !('mainLabel' in rowConfig)) {
                                            return null;
                                        }
                                        
                                        const value = getFormattedValue(rowConfig.dataType, rowConfig.dataValue);
                                        const isLastRow = index === array.length - 1;
                                        const className = isLastRow ? "" : "mb-3";
                                        
                                        return renderFinancialRow({
                                            ...rowConfig,
                                            value,
                                            className,
                                            //colProps,
                                        });
                                    })}
                            </Row>
                        </Container>
                    </Card.Body>
                </>
            }
        </Card>
    }

    const RequisitionCard = () => {
        if(PSM === "M") return <Card>THIS CARD SHOULD NOT BE SHOWING</Card>
        const requisitions=DataReaderObj.getDefectInformation().filter(defect => defect.name > "");
        const reqDate = (provinceCode === "MB" || provinceCode === "SK")
            ? DataReaderObj.getReverseTrustLetterDate()
            : DataReaderObj.getRequisionDate();
        const receiveDate=DataReaderObj.getRequisitionReceivedDate();
        const sentDate=DataReaderObj.getRequisitionSentDate();
        const responseDate=DataReaderObj.getRequisitionResponseDate();
        const requisitionConst = {
            ON: {
                P: {title: "Requisition", headName: "Requisition", contentReqName: "Requisition", contentSendName: "Sent", tabLabel: "Requisition"},
                S: {title: "Response to Requisition", headName: "Response to Requisition", contentReqName: "Requisition", contentSendName: "Received", tabLabel: "Response to Requisition"},
                content: {reqDate: true, sendDate: true, status: true, emptyLinkText: "Add Requisition Date", emptyNavLabel: "Contract", emptyController: { tabName: "Basic", tabNumber: 2 }, resolveStr: "Resolved", outstandingStr: "Outstanding"}
            },
            BC: {
                P: {title: "Undertaking", headName: "Undertaking", contentReqName: "Undertaking", contentSendName: "Undertaking Response Letter", tabLabel: "Undertaking"},
                S: {title: "Response to Undertaking", headName: "Response to Undertaking", contentReqName: "Undertaking Letter Received", contentSendName: "Undertaking Response Letter", tabLabel: "Response to Undertaking"},
                content: {reqDate: true, sendDate: true, status: true, emptyLinkText: "Add Undertaking Date", emptyNavLabel: "Basic", emptyController: { tabName: "Basic", tabNumber: 0 }, resolveStr: "clear", outstandingStr: "pending"}
            },
            AB: {
                P: {title: "Description Letter", headName: "Description Letter", contentReqName: "", contentSendName: "", tabLabel: "Description Letter"},
                S: {title: "Response to Trust Letter", headName: "Response to Trust Letter", contentReqName: "", contentSendName: "", tabLabel: "Response to Trust Letter"},
                content: {reqDate: false, sendDate: false, status: true, emptyLinkText: "Add Issue", emptyNavLabel: "Issues", emptyController: { tabName: "Issues", tabNumber: 0 }, resolveStr: "Resolved", outstandingStr: "Outstanding"}
            },
            NS: {
                P: {title: "Objection", headName: "Objection", contentReqName: "Objection Date", contentSendName: "Sent", tabLabel: "Objection"},
                S: {title: "Reponse to Objection", headName: "Response to Objection", contentReqName: "Objection Date", contentSendName: "Received", tabLabel: "Response to Objection"},
                content: {reqDate: true, sendDate: true, status: true, emptyLinkText: "Add Objection Date", emptyNavLabel: "Contract", emptyController: { tabName: "Basic", tabNumber: 2 }, resolveStr: "Resolved", outstandingStr: "Outstanding"}
            },
            MB: {
                P: {title: "Trust Letter", headName: "Trust Letter", contentReqName: "Trust Letter Date", contentSendName: "", tabLabel: "Trust Letter"},
                S: {title: "Trust Letter", headName: "Trust Letter", contentReqName: "Trust Letter Date", contentSendName: "", tabLabel: "Response to Trust Letter"},
                content: {reqDate: true, sendDate: false, status: true, emptyLinkText: "Add Trust Letter Date", emptyNavLabel: "Basic", emptyController: { tabName: "Basic", tabNumber: 0 }, resolveStr: "Permitted Encumbrance", outstandingStr: "Non-Permitted Encumbrance", resolveTitle: "Permitted", outstandingTitle: "Non-Permitted"}
            },
            SK: {
                P: {title: "Trust Letter Reconciliation", headName: "Trust Letter Reconciliation", contentReqName: "Trust Letter Date", contentSendName: "", tabLabel: ""},
                S: {title: "Trust Letter Reconciliation", headName: "Trust Letter Reconciliation", contentReqName: "Trust Letter Date", contentSendName: "", tabLabel: ""},
                content: {reqDate: true, sendDate: false, status: false, emptyLinkText: "Add Trust Letter Date", emptyNavLabel: PSM === "S" ? "Trust Letter" : "Trust Letter Reconciliation", emptyController: { tabName: "Issues", tabNumber: 2 }, resolveStr: "Resolved", outstandingStr: "Outstanding"}
            },
        }
        const defaultConst = {title: "", headName: "", contentReqName: "", contentSendName: "", tabLabel: ""};
        const defaultContent =  {reqDate: false, sendDate: false, status: false, emptyLinkText: "", emptyNavLabel: "", emptyController: { tabName: "Basic", tabNumber: 0 }, resolveStr: "", outstandingStr: ""}
        const constData = requisitionConst[provinceCode][PSM] || defaultConst;
        const contentData = requisitionConst[provinceCode].content || defaultContent;
        const isReqDateMissing = contentData.reqDate && !(reqDate > "");
        let reqCompleted = requisitions.filter(req => req.ReqResult === contentData.resolveStr);
        let reqUncompleted = requisitions.filter(req => req.ReqResult === contentData.outstandingStr);
        let sendTitle: string, sendDate: string;
        switch (PSM){
            case "P":
                sendTitle = 'Received';
                sendDate = sentDate;
                break;
            case "S":
                sendTitle = 'Sent';
                sendDate = receiveDate;
                break;
        }
        if(provinceCode === "BC") sendDate = responseDate;
        return <Card>
            {(isReqDateMissing) ?
                    <EmptyField
                        icon="fa-file-slash"
                        txt={constData.title}
                        linkText={contentData.emptyLinkText}
                        navFunc={() => navPage(contentData.emptyNavLabel, contentData.emptyController)}
                    />
                :
                <>
                    <Card.Header>
                        <Card.Title>
                            <i className='fa-duotone fa-square-check'></i>
                            {constData.title}
                        </Card.Title>
                        <Button variant="outline-primary" onClick={() => navigate("Issues")}><i className='fa-regular fa-up-right-from-square'></i>Issues</Button>
                    </Card.Header>
                    <Card.Body>
                        <Container>
                            <Row className="mb-3">
                                {contentData.reqDate && 
                                <Col xs={6} className={styles.glow} onClick={() => navPage(contentData.emptyNavLabel, contentData.emptyController)}>
                                    <Form.Label style={{ margin: '0'}}>{constData.contentReqName}</Form.Label>
                                    <span className={styles.editIcon}>{formatDate(reqDate)}</span>
                                </Col>}

                                {contentData.sendDate && 
                                <Col xs={6} className={styles.glow} onClick={() => navPage(contentData.emptyNavLabel, contentData.emptyController)}>
                                    <Form.Label style={{ margin: '0'}}>{constData.contentSendName}</Form.Label>
                                    <span className={styles.editIcon}>{formatDate(sendDate)}</span>
                                </Col>}
                            </Row>

                            {contentData.status &&
                                <Row>
                                    <Col>
                                        <Form.Label style={{ margin: '0'}}>
                                            {provinceCode === "MB" ? "Encumbrances" : "Status"}
                                        </Form.Label>
                                        <Badge pill bg="warning">{`${reqUncompleted.length} ${contentData.outstandingTitle || contentData.outstandingStr}`}</Badge>
                                        <Badge pill bg="success">{`${reqCompleted.length} ${contentData.resolveTitle || contentData.resolveStr}`}</Badge>
                                    </Col>
                                </Row>
                            }

                            {/* {contentContent} */}
                        </Container>
                    </Card.Body>
                </>
            }
        </Card>
    }

    const RegistrationCard = () => {
        if(provinceCode === "BC") return <WebFilingCard/>;
        // if(!props.registrationDetails.length) props.registrationDetails = [{date: "", desc: "", instrumentNo: ""}]
        const registrationDetails=DataReaderObj.getRegistrationSummary();
        let realRegistrations = registrationDetails.filter(e => e.desc > "" && e.instrumentNo > "");
        let regHeader = "Registration", headerLinks = <></>;
        switch(provinceCode){
            case "ON":
                regHeader = "e-reg™";
                headerLinks = <>
                    {PSM === "S" || <Button variant="outline-primary" onClick={() => navigate("PIPS", "Registration")}>
                        <i className="fa-regular fa-file-lines"></i>
                        PIPS 5.0.1
                    </Button>}
                    <Button variant="outline-primary" onClick={() => navigate("Export to e-reg", "Registration")}>
                        <i className="fa-regular fa-file-export"></i>
                        Export
                    </Button></>
                break;
            case "MB":
                headerLinks = <>
                    <Button variant="outline-primary" onClick={() => nav(getNav("Registration", "Teranet"))}>
                        <i className="fa-regular fa-file-export"></i>
                        Teranet<sup>&reg;</sup>
                    </Button>
                </>
        }
        return( <Card>
            {!(registrationDetails.length) ?
                <EmptyField
                    icon="fa-file-slash" 
                    txt="Registration" 
                    linkText='Add Registration' 
                    navFunc={() => nav({component: "main", action: "PopUpRegistrationDetails"})}
                >
                    {headerLinks}
                </EmptyField>
                :
                <>
                    <Card.Header>
                        <Card.Title>
                            <i className='fa-duotone fa-cabinet-filing'></i>
                            {regHeader}
                        </Card.Title>
                        {headerLinks}
                        {/* <i onClick={() => nav({component: "main", action: "PopUpRegistrationDetails"})} className={"fa-duotone fa-plus-circle " + styles.add}></i> */}
                    </Card.Header>

                    <Card.Body>
                        <Row className="gx-2">
                            <Col>
                                <Form.Label>Description</Form.Label>
                            </Col>
                            <Col className="text-end">
                                <Form.Label>Instrument No.</Form.Label>
                            </Col>
                            <Col className="text-end">
                                <Form.Label>Date</Form.Label>
                            </Col>
                        </Row>

                        {registrationDetails.map((val, index) => 
                            <Row key={index} className={"gx-2 " + styles.glow} onClick={() => nav({component: "main", action: "PopUpRegistrationDetails"})}>
                                <Col>
                                    <span className={styles.editIcon}>{CheckEmpty(val.desc)}</span>
                                </Col>
                                <Col className="text-end">{CheckEmpty(val.instrumentNo)}</Col>
                                <Col className="text-end">{formatDate(val.date)}</Col>
                            </Row>
                        )}
                    </Card.Body>
                </>
            }
        </Card>)
    }

    const WebFilingCard = () => {
        const filings = DataReaderObj.getWebFilings();
        return <Card>
            {!(filings.length) ?
                <EmptyField icon="fa-file-slash" txt="Registration" linkText='Add Web Filing' navFunc={() => navPage("Web Filing", {tabName: "Registrations", tabNumber: 1})}>
                        <Button variant="outline-primary" onClick={() => navigate("LOTR", "Registration")}>
                            <i className="fa-regular fa-file-export"></i>
                            LOTR
                        </Button>
                </EmptyField>
                :
                <>
                    <Card.Header>
                        <Card.Title>
                        <i className='fa-duotone fa-cabinet-filing'></i>
                            Web Filing
                        </Card.Title>
                        <Button variant="outline-primary" onClick={() => navigate("LOTR", "Registration")}>
                            <i className="fa-regular fa-file-export"></i>
                            LOTR
                        </Button>
                        <i onClick={() => navPage("Web Filing", {tabName: "Registrations", tabNumber: 1})} className={"fa-solid fa-plus-circle " + styles.add}></i>
                    </Card.Header>

                    <Card.Body>
                        <Row>
                            <Col>
                                <Form.Label>Filing</Form.Label>
                            </Col>
                        </Row>

                        {filings.map(e => 
                            <Row onClick={() => navPage("Web Filing", {tabName: "Registrations", tabNumber: 1})} className={"gx-2 " + styles.glow}>
                                <Col>{CheckEmpty(e)}</Col>
                            </Row>
                        )}
                    </Card.Body>
                </>
            }
        </Card>

    }

    const AdditionalPartiesCard = () => {
        const thirdParty = DataReaderObj.getThirdPartySummary();
        const addOptions = (detailsList.find(node => node.id === "Third")?.addOptions || []).filter(node => (!node.appCode || node.appCode.includes(appCode)) && (!node.PSM || node.PSM.includes(PSM)));

        const addMenuNav = (navObject: NavObjectType) => {
            let controller = navObject.controller;
            nav(navObject);
        }

        const navAdditionalParty = (party: ThirdPartySummaryType) => {
            return () => navPage(party.name, {tabName: party.personType, tabNumber: party.personType === PopupPageId.OtherParty? party.index:undefined})
        }

        let partyTitle = {
            "MortgageBroker": "Mortgage Broker",
            "RelocationCompany": "Relocation Company",
            "InsuranceAgent": "Fire Insurance",
            "RealEstateAgent": "Real Estate Agent",
        }
        switch(provinceCode){
            case "AB":
                partyTitle["RealEstateAgent"] = "Buying Agent";
                partyTitle["PurchaserRealEstateAgent"] = "Selling Agent";
                break;
            case "MB":
            case "BC":
                partyTitle["RealEstateAgent"] = "Listing Agent";
                partyTitle["PurchaserRealEstateAgent"] = "Selling Agent";
                break;
        }

        return <Card>
            {!thirdParty.length? 
                <EmptyField icon="fa-user-slash" txt="Additional Parties" linkText='' navFunc={() => alert("what is this button supposed to do")}>
                    <a className={styles.underline} style={{display: "flex", alignItems: "center", justifyContent: "center"}}><OptionsDropdown options={addOptions} nav={addMenuNav} text={"Add Additional Party"}/></a>
                </EmptyField>
                :
                <>
                    <Card.Header>
                        <Card.Title>
                            <i className='fa-duotone fa-people-group'></i>
                            Additional Parties
                        </Card.Title>
                        <OptionsDropdown options={addOptions} nav={addMenuNav}/>
                    </Card.Header>

                    <Card.Body>
                        {thirdParty.map((val, index) => 
                            <Row className={"gx-1 " + styles.glow} key={index} onClick={navAdditionalParty(val)}>
                                <Col xs={12}>
                                    <Form.Label style={{ margin: '0'}}>
                                        {partyTitle[val.type] || val.type || "Type of Additional Party"}
                                    </Form.Label>
                                </Col>

                                <Col className="text-truncate">
                                    <span className={styles.editIcon}>
                                        {val.name}
                                    </span>
                                </Col>

                                <Col xs={"auto"}>
                                    <OverlayTrigger placement="bottom-end" overlay={<Tooltip>{formatPhoneNumber(val.phone) || "No Phone Number Added"}</Tooltip>}>
                                        <i className={`fa-duotone fa-phone ${!val.phone ? "status-secondary-light" : ""} ${styles.contactButton}`}></i>
                                    </OverlayTrigger>
                                </Col>
                        
                                <Col xs={"auto"}>
                                    <OverlayTrigger placement="bottom-end" overlay={<Tooltip>{val.email || "No Email Address Added"}</Tooltip>}>
                                        <i className={`fa-duotone fa-envelope ${!val.email ? "status-secondary-light" : ""} ${styles.contactButton}`}></i>
                                    </OverlayTrigger>
                                </Col>

                                <Col xs={"auto"}>
                                    <OverlayTrigger placement="bottom-end" overlay={<Tooltip>{val.address || "No Address Added"}</Tooltip>}>
                                        <i className={`fa-duotone fa-location-dot ${!val.address ? "status-secondary-light" : ""} ${styles.contactButton}`}></i>
                                    </OverlayTrigger>
                                </Col>

                                {val.company > "" &&
                                    <Col xs={12}>
                                        <span className={styles.subtitle}>{val.company}</span>
                                    </Col>
                                }
                            </Row>
                        )}
                    </Card.Body>
                </>
                }
        </Card>
    }

    const navToDetails = (navObj: NavObjectType): void => {
        if(navObj == null) return;
        // dispatch(setSideMenuIntention(fileTabVal.DETAILS, navObj));
        // console.log("set dispatch")
        // console.log(navObj);
        nav(navObj)
        // console.log("bruh")
    }

    function navPage (label: string, controller?: purfileController, reactFrame?: string): void {
        let navObj: NavObjectType = {
            component: "main",
            url: "lfmaster.asp",
            params: {
                "tb": "TPAppXML",
                "formasp": "purfileedit",
                "id": appID,
                "PSM": PSM,
            },
            controller: controller,
            reactFrame: reactFrame,
            // scrollToId: "contentHolder",
            label: label,
        }
        // console.log("navpage", navObj);
        navToDetails(navObj);
    }

    function navPopup(controller: purfileController) { // idk whats going on here
        let navObj: NavObjectType = {
            component: "main",
            url: "lfmaster.asp",
            params: {
                "tb": "TPAppXML",
                "formasp": "purfileedit",
                "id": appID,
                "PSM": PSM,
            },
            controller: {
                tabName: "Issues",
                tabNumber: 0,
            },
            reactFrame: 'similiarnames',
            popup: true,
            isModal: true,
            popupStyle: "xlargelong",
        }
        console.log("navpage", navObj);
        nav(navObj);
    }

    function navigate (label: string, topLevel?: string): void {
        let navObj = getNav(topLevel || label, topLevel && label)

        // let c = getController(topLevel || label, topLevel && label)
        navPage(label, navObj.controller, navObj.reactFrame)
    }

    function checkDetailsListAppCode(appCode: string, list: AppCodes[]){
        // console.log(appCode, list)
        return list.includes(`RPW${appCode==="ON"?"":appCode}` as AppCodes);
    }

    function formatMoney(value: string | number): string {
        if(!value) return "$0.00";
        if(typeof value === "string"){
            value = parseFloat(value.replace(/[$, ]/g, ""));
        }
        return value.toLocaleString('en-US', {style: 'currency', currency: 'USD'});
    }

    function formatDate(str: string): string {
        if(!str) return "Date not set";
        return FormatDateDataType(str, "dfca")
    }

    const EmptyField = (props: {icon: string, txt: string, linkText: string, navFunc: () => void, children?: ReactNode}) => {
        return (
            <div className={styles.emptyField}>
                <div className={styles.emptyFieldInner}>
                    <i className={`fa-solid ${props.icon} ${styles.emptyIcon}`}></i>

                    <h3 className="mb-2">{props.txt}</h3>
                    <div className="mb-2">
                        {props.children}
                    </div>
                    <div>                    
                        <a className={styles.underline} onClick={props.navFunc}>{props.linkText}</a>
                    </div>
                </div>
            </div>
        );
    }

    const SellerLegalCard = () => {
        let name = DataReaderObj.getVendorLawyerName();
        let phone = DataReaderObj.getVendorLawyerField("DirectPhone");
        let email = DataReaderObj.getVendorLawyerField("FirmEmail");
        const navTarget = () => navToDetails({            
            component: "main",
            url: "lfmaster.asp",
            params: {
                "tb": "TPAppXML",
                "formasp": "purfileedit",
                "id": appID,
                "PSM": PSM,
            },
            label: "Basic",
            controller: {
                tabName: "Basic",
                tabNumber: 0,
            },
            scrollToId: "VendorSolicitor",
        })
        return <Card>
            <Card.Header>
                <Card.Title>
                    <i className='fa-duotone fa-scale-balanced'></i>
                    Seller Legal Representation
                </Card.Title>
            </Card.Header>
            <Card.Body>
                {name > ""?
                <Row className={"gx-1 mb-3 " + styles.glow} onClick={navTarget}>
                    <Col>
                        <span className={styles.editIcon}>
                            {name}
                            <Badge pill bg="info" style={{marginLeft: '8px'}}>Lawyer</Badge>
                        </span>
                    </Col>
                    <Col xs={"auto"}>
                        <OverlayTrigger placement="bottom-end" overlay={<Tooltip>{formatPhoneNumber(phone) || "No Phone Number"}</Tooltip>}>
                            <i className={`fa-duotone fa-phone ${!phone ? "status-secondary-light" : ""} ${styles.contactButton}`}></i>
                        </OverlayTrigger>
                    </Col>
                    <Col xs={"auto"}>
                        <OverlayTrigger placement="bottom-end" overlay={<Tooltip>{email || "No Email"}</Tooltip>}>
                            <i className={`fa-duotone fa-envelope ${!email ? "status-secondary-light" : ""} ${styles.contactButton}`}></i>
                        </OverlayTrigger>
                    </Col>
                </Row>
                :
                <Row className="gx-1 mb-3"><Col><PreIcon cond={false}><a className={styles.underline} onClick={navTarget}>Add Lawyer</a></PreIcon></Col></Row>}
            </Card.Body>
        </Card>
    }

    const OtherLenderLegalCard = (props: {lenderSolicitors: MortgageSolicitorType[]}) => {
        return <Card>
            <Card.Header>
                <Card.Title>    
                    <i className='fa-duotone fa-scale-balanced'></i>
                    {"Other Lender(s) Legal Representation"}
                </Card.Title>
            </Card.Header>
            <Card.Body>
                {props.lenderSolicitors.map((val, index) => {
                    return <Row key={index} className={"gx-1 mb-3 " + styles.glow} onClick={() => navPage(val.lender, {tabName: "Mortgages", tabNumber: val.index})}>
                        <Col>
                            <span className={styles.editIcon}>
                                {val.name}
                            </span>
                        </Col>
                        <Col xs={"auto"}>
                            <OverlayTrigger placement="bottom-end" overlay={<Tooltip>{formatPhoneNumber(val.phone) || "No Phone Number"}</Tooltip>}>
                                <i className={`fa-duotone fa-phone ${!val.phone ? "status-secondary-light" : ""} ${styles.contactButton}`}></i>
                            </OverlayTrigger>
                        </Col>
                        <Col xs={"auto"}>
                            <OverlayTrigger placement="bottom-end" overlay={<Tooltip>{val.email || "No Email"}</Tooltip>}>
                                <i className={`fa-duotone fa-envelope ${!val.email ? "status-secondary-light" : ""} ${styles.contactButton}`}></i>
                            </OverlayTrigger>
                        </Col>

                        <Col xs={12}>
                            <span className={styles.subtitle}>{val.lender}</span>
                        </Col>
                    </Row>
                })}
            </Card.Body>
        </Card>
    }

    return <>
        <Container className={styles.summarygrid + " gx-0 gy-2"} fluid>
            {showWelcomeAlert && (
                <Alert variant="info" onClose={handleWelcomeAlertDismiss} className="mb-2">
                    <div className="d-flex align-items-center justify-content-between">
                        <div>
                            <span>Introducing the new File Summary — a streamlined and comprehensive view of your file.</span>
                            <Button variant="link" className="p-0" onClick={() => window.open(SUMMARY_LEARN_MORE_URL, '_blank')}>
                                Learn more
                            </Button>
                        </div>
                        <Button
                            variant="link"
                            className="p-0"
                            aria-label="Dismiss"
                            onClick={handleWelcomeAlertDismiss}
                        >
                            <i className="fa-solid fa-xmark"></i>
                        </Button>
                    </div>
                </Alert>
            )}

            <Row className="g-2">
                <Col xs={4}>
                    <PropertyCard/>
                </Col>

                <Col xs={4}>
                    <Row className="g-0" style={{height: '100%', gap: '0.5rem'}}>
                        {hideBuyerTile || <Col xs={12}>
                            <BuyerCard/>
                        </Col>}
                        {hideSellerTile || <Col xs={12}>
                            <SellerCard/>
                        </Col>}
                        {showSellerLegal && <Col xs={12}>
                            <SellerLegalCard/>
                        </Col>}
                        {showOtherLegal && <Col xs={12}>
                            <OtherLenderLegalCard lenderSolicitors={lenderSolicitors}/>
                        </Col>}
                    </Row>
                </Col>

                <Col xs={4}>
                    <Row className="g-0" style={{height: '100%', gap: '0.5rem'}}>
                        <Col xs={12}>
                            <FinancialCard/>
                        </Col>

                        {PSM === "M" || 
                            <Col xs={12}>
                                <RequisitionCard/>
                            </Col>
                        }
                    </Row>
                </Col>

                <Col xs={8}>
                    <Row className="g-0" style={{height: '100%', gap: '0.5rem'}}>
                        <Col xs={12}>
                            <MortgageCard/>
                        </Col>

                        <Col xs={12}>
                            <RegistrationCard/>
                        </Col>
                    </Row>
                </Col>

                <Col xs={4}>
                    <AdditionalPartiesCard/>
                </Col>
            </Row>
        </Container>
    </>
}

export default Summary;
